// frontend/src/pages/ProfessionalJourney.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import {
    DocumentTextIcon,
    ShareIcon,
    BriefcaseIcon,
    CheckCircleIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import CareerDirectionCard from '../features/careerDirection/CareerDirectionCard';
import LinkedInPostsTab from '../features/careerJourney/LinkedInPostsTab';

const ProfessionalJourney = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [posts, setPosts] = useState([]);
    const [milestones, setMilestones] = useState([]);
    const [liveMilestones, setLiveMilestones] = useState([]);
    const [readiness, setReadiness] = useState(null);
    const [stats, setStats] = useState(null);
    const [projects, setProjects] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [questions, setQuestions] = useState([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const results = await Promise.allSettled([
                axios.get('/career/linkedin/posts'),
                axios.get('/career/linkedin/milestones'),
                axios.get('/career/readiness'),
                axios.get('/career/stats'),
                axios.get('/career/github/projects'),
                axios.get('/career/journey/live-milestones'),
                axios.get('/career/journey/reflection-questions')
            ]);

            const value = (i) => (results[i].status === 'fulfilled' ? results[i].value?.data?.data : null);
            const failed = results.find((r) => r.status === 'rejected');

            setPosts(value(0) || []);
            setMilestones(value(1) || []);
            setReadiness(value(2) || null);
            setStats(value(3) || null);
            setProjects(value(4) || []);
            setLiveMilestones(value(5) || []);
            setQuestions(value(6) || []);

            if (failed) {
                const msg = failed.reason?.response?.data?.message || failed.reason?.message || 'Failed to load professional journey';
                toast.error(msg);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load professional journey');
        } finally {
            setLoading(false);
        }
    };

    const handleGeneratePost = async (milestoneId, style) => {
        setGenerating(true);
        try {
            const response = await axios.post(`/career/linkedin/generate/${milestoneId}`, { style });
            const data = response.data.data;
            await axios.post('/career/linkedin/posts', {
                title: data.milestone || 'LinkedIn Post',
                content: data.content,
                milestone_type: 'linkedin',
                milestone_id: milestoneId,
                status: 'draft',
                share_mode: 'portfolio_only',
                content_sensitivity: 'generic_milestone'
            });
            toast.success('Your post is ready — review it, edit if you like, then submit');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to generate post');
        } finally {
            setGenerating(false);
        }
    };

    const handleSavePost = async (postId, content) => {
        try {
            await axios.put(`/career/linkedin/posts/${postId}`, { content });
            toast.success('Post saved');
            loadData();
            return true;
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save the post');
            return false;
        }
    };

    const handleSubmitPost = async (postId) => {
        try {
            await axios.post(`/career/linkedin/posts/${postId}/submit`);
            toast.success('Submitted for review / ready to share');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to submit');
        }
    };

    const handlePublishPost = async (postId) => {
        try {
            await axios.post(`/career/linkedin/posts/${postId}/publish`);
            toast.success('Marked as published (add your LinkedIn URL when ready)');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Cannot publish yet — approval may be required');
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-72" />
                <div className="skeleton h-32" />
                <span className="sr-only">Loading professional journey…</span>
            </div>
        );
    }

    const tabs = [
        { id: 'overview', label: 'Overview', icon: SparklesIcon },
        { id: 'document', label: 'Document Journey', icon: DocumentTextIcon },
        { id: 'linkedin', label: 'LinkedIn Posts', icon: ShareIcon },
        { id: 'jobs', label: 'Opportunities', icon: BriefcaseIcon, status: 'coming-soon' },
    ];

    return (
        <div className="w-full space-y-6">
            <PageHeader
                title="Professional Journey"
                description="Document real milestones, prepare LinkedIn updates, and build social proof — with approval when a named business is involved."
            />

            <CareerDirectionCard />

            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Metric label="Milestones documented" value={stats.milestonesDocumented || 0} />
                    <Metric label="Published posts" value={stats.approvedPublicPosts ?? stats.publishedPosts ?? 0} />
                    <Metric label="Projects documented" value={stats.projectsDocumented || 0} />
                    <Metric label="Skills added" value={stats.skillsAdded || 0} />
                    <Metric label="Certificate shared" value={stats.certificateShared ? 'Yes' : 'Not yet'} />
                </div>
            )}

            <div className="card overflow-hidden">
                <div className="flex border-b border-border px-6 overflow-x-auto">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-3 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                                activeTab === tab.id
                                    ? 'border-brand-700 text-brand-800'
                                    : 'border-transparent text-ink-500 hover:text-ink-800'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            <span>{tab.label}</span>
                            {tab.status === 'coming-soon' && (
                                <span className="badge badge-neutral shrink-0 !px-1.5 !py-0 !text-[10px]">Soon</span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="p-6">
                    {activeTab === 'overview' && (
                        <OverviewTab liveMilestones={liveMilestones} readiness={readiness} />
                    )}
                    {activeTab === 'document' && (
                        <DocumentTab
                            liveMilestones={liveMilestones}
                            questions={questions}
                            onSaved={loadData}
                        />
                    )}
                    {activeTab === 'linkedin' && (
                        <LinkedInPostsTab
                            posts={posts}
                            milestones={milestones}
                            generating={generating}
                            onGenerate={handleGeneratePost}
                            onSubmit={handleSubmitPost}
                            onPublish={handlePublishPost}
                            onSave={handleSavePost}
                        />
                    )}
                    {activeTab === 'jobs' && <JobTab />}
                </div>
            </div>
        </div>
    );
};

const Metric = ({ label, value }) => (
    <div className="card p-4 text-center">
        <p className="text-xs font-medium text-ink-500 uppercase">{label}</p>
        <p className="text-2xl font-bold text-ink-950 mt-1">{value}</p>
    </div>
);

const OverviewTab = ({ liveMilestones, readiness }) => (
    <div className="space-y-6">
        <div>
            <h3 className="text-base font-semibold text-ink-950 mb-3">Live milestones</h3>
            <p className="text-xs text-ink-500 mb-4">
                Earned from real progress only — you cannot manually claim completion.
            </p>
            <ul className="space-y-2">
                {(liveMilestones || []).map((m) => (
                    <li key={m.key} className="flex items-start gap-3 p-3 bg-ink-50 rounded-lg border border-gray-100">
                        <CheckCircleIcon className={`w-5 h-5 mt-0.5 ${m.completed ? 'text-green-600' : 'text-gray-300'}`} />
                        <div>
                            <p className={`text-sm font-medium ${m.completed ? 'text-ink-950' : 'text-ink-500'}`}>{m.label}</p>
                            <p className="text-xs text-ink-500">
                                {m.completed
                                    ? (m.documented ? 'Documented' : 'Ready to document')
                                    : 'Not earned yet'}
                            </p>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
        {readiness && (
            <div className="border-t border-gray-100 pt-4">
                <p className="text-sm font-medium text-ink-950">
                    Career readiness: <span className="capitalize">{String(readiness.overall_readiness || '').replace(/_/g, ' ') || 'Not assessed'}</span>
                </p>
            </div>
        )}
        {!readiness && (
            <p className="text-xs text-ink-500">No career readiness assessment on file yet.</p>
        )}
    </div>
);

const DocumentTab = ({ liveMilestones, questions, onSaved }) => {
    const [milestoneKey, setMilestoneKey] = useState('');
    const [answers, setAnswers] = useState({});
    const [sensitivity, setSensitivity] = useState('generic_milestone');
    const [shareMode, setShareMode] = useState('portfolio_only');
    const [linkedinContent, setLinkedinContent] = useState('');
    const [saving, setSaving] = useState(false);
    const [celebration, setCelebration] = useState(null);

    const documentable = (liveMilestones || []).filter((m) => m.canDocument || (m.completed && !m.documented));

    const handleSave = async () => {
        setSaving(true);
        setCelebration(null);
        try {
            const res = await axios.post('/career/journey/document', {
                milestone_key: milestoneKey || null,
                answers,
                content_sensitivity: sensitivity,
                share_mode: shareMode,
                linkedin_content: linkedinContent || undefined,
                title: milestoneKey
                    ? `Documented: ${(liveMilestones.find((m) => m.key === milestoneKey) || {}).label || milestoneKey}`
                    : 'Journey documentation'
            });
            setCelebration(res.data.data?.celebration || 'Saved!');
            toast.success('Journey documented');
            setAnswers({});
            setLinkedinContent('');
            onSaved();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to document');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4 max-w-2xl">
            <h3 className="text-base font-semibold text-ink-950">Document Your Journey</h3>
            {celebration && (
                <div className="bg-green-50 border border-green-200 text-green-800 text-sm p-3 rounded-lg">
                    {celebration}
                </div>
            )}
            <label className="block text-xs font-medium text-ink-700">
                Earned milestone
                <select
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    value={milestoneKey}
                    onChange={(e) => setMilestoneKey(e.target.value)}
                >
                    <option value="">Select a completed milestone</option>
                    {documentable.map((m) => (
                        <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                </select>
            </label>
            {(questions || []).map((q) => (
                <label key={q} className="block text-xs font-medium text-ink-700">
                    {q}
                    <textarea
                        className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        rows={2}
                        value={answers[q] || ''}
                        onChange={(e) => setAnswers({ ...answers, [q]: e.target.value })}
                    />
                </label>
            ))}
            <label className="block text-xs font-medium text-ink-700">
                LinkedIn draft (editable)
                <textarea
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
                    rows={4}
                    value={linkedinContent}
                    onChange={(e) => setLinkedinContent(e.target.value)}
                    placeholder="Optional — leave blank and we'll write a LinkedIn post from your answers, with the LancersTech hashtags"
                />
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
                <label className="block text-xs font-medium text-ink-700">
                    Content type
                    <select
                        className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={sensitivity}
                        onChange={(e) => setSensitivity(e.target.value)}
                    >
                        <option value="learning_only">Learning-only</option>
                        <option value="generic_milestone">Generic internship milestone</option>
                        <option value="anonymous_business">Anonymous real-business learning</option>
                        <option value="named_business">Named business / client</option>
                        <option value="sensitive">Sensitive (internal only)</option>
                    </select>
                </label>
                <label className="block text-xs font-medium text-ink-700">
                    Share intent
                    <select
                        className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={shareMode}
                        onChange={(e) => setShareMode(e.target.value)}
                    >
                        <option value="portfolio_only">Internal / LMS only</option>
                        <option value="social">Social share (when approved)</option>
                    </select>
                </label>
            </div>
            {sensitivity === 'named_business' && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
                    Named business posts require mentor review, then business/program approval before public sharing.
                </p>
            )}
            {sensitivity === 'sensitive' && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 p-2 rounded">
                    Sensitive content stays internal — public sharing is disabled.
                </p>
            )}
            <button
                type="button"
                disabled={saving || Object.keys(answers).length === 0}
                onClick={handleSave}
                className="px-4 py-2 bg-brand-700 text-white text-sm font-medium rounded-md hover:bg-brand-800 disabled:opacity-50"
            >
                {saving ? 'Saving…' : 'Save documentation'}
            </button>
        </div>
    );
};

const JobTab = () => {
    const [jobs, setJobs] = useState([]);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [jobsRes, appsRes] = await Promise.all([
                    axios.get('/career/jobs'),
                    axios.get('/career/applications')
                ]);
                setJobs(jobsRes.data.data || []);
                setApplications(appsRes.data.data || []);
            } catch {
                toast.error('Failed to load opportunities');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <p className="text-ink-500 text-sm text-center">Loading…</p>;
    const appliedIds = applications.map((a) => a.opportunity_id);

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-ink-950">Opportunities</h3>
                <span className="badge badge-neutral !px-2 !py-0.5 !text-[10px]">Coming Soon</span>
            </div>
            {jobs.length === 0 ? (
                <p className="text-ink-500 text-sm py-4 text-center">No active listings.</p>
            ) : (
                jobs.map((job) => (
                    <div key={job.id} className="rounded-xl border border-border p-4 flex justify-between gap-3">
                        <div>
                            <h4 className="font-bold text-ink-950 text-sm">{job.title}</h4>
                            <p className="text-xs text-ink-600">{job.company} • {job.location || 'Remote'}</p>
                        </div>
                        {appliedIds.includes(job.id) ? (
                            <span className="text-xs text-green-700">Applied</span>
                        ) : (
                            <button
                                className="px-3 py-1.5 bg-brand-700 text-white text-xs rounded"
                                onClick={async () => {
                                    try {
                                        await axios.post(`/career/jobs/${job.id}/apply`);
                                        toast.success('Application submitted');
                                        setApplications([...applications, { opportunity_id: job.id }]);
                                    } catch (e) {
                                        toast.error(e.response?.data?.message || 'Failed');
                                    }
                                }}
                            >
                                Apply
                            </button>
                        )}
                    </div>
                ))
            )}
        </div>
    );
};

export default ProfessionalJourney;
